import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { ThemeContext } from 'styled-components';
import { getFonts } from '../../../../../../styles/variables';
import { HeaderCell, StyledHeader, StyledTable } from '.';
import { LocationRow } from './location-row';
import { SortableHeader } from './sortable-header';
import { Checkbox } from '@blueprintjs/core';
import _ from 'lodash';

const LocationTable = ({
  data,
  selectable,
  selected,
  onToggleRow,
  onToggleAll,
  tagsColumn,
  tagFilterPredicate,
  stickyHeader,
}) => {
  const theme = useContext(ThemeContext);
  const { headingFont } = getFonts(theme);
  const [filteredData, setFilteredData] = useState();
  const [filterOptions, setFilterOptions] = useState({
    name: true,
  });
  const headerCheckboxRef = useRef(null);

  function updateFilter(key, value) {
    setFilterOptions({ [key]: value });
  }

  function orderBy(keys, value) {
    return _.orderBy(data, keys, [value ? 'asc' : 'desc']);
  }

  const sortRows = (key, value) => {
    switch (key) {
      case 'name':
        return orderBy([key], value);
      case 'recordingsUp':
        return orderBy([key], value);
      case 'recordingPercent':
        return orderBy([key], value);
      case 'status':
        return orderBy([key, 'name'], value);
      default:
        break;
    }
  };

  useEffect(() => {
    setFilteredData(
      sortRows(
        Object.keys(filterOptions)[0],
        filterOptions[Object.keys(filterOptions)[0]],
      ),
    );
  }, [filterOptions, data]);

  const visibleData = useMemo(() => {
    const list = filteredData || [];
    if (!tagFilterPredicate) return list;
    return list.filter(tagFilterPredicate);
  }, [filteredData, tagFilterPredicate]);

  const visibleIds = useMemo(
    () => (visibleData || []).map((d) => d.id),
    [visibleData],
  );

  const allSelected = useMemo(() => {
    if (!selectable || !selected) return false;
    if (visibleIds.length === 0) return false;
    return visibleIds.every((id) => selected.has(id));
  }, [selectable, selected, visibleIds]);

  const someSelected = useMemo(() => {
    if (!selectable || !selected) return false;
    return visibleIds.some((id) => selected.has(id)) && !allSelected;
  }, [selectable, selected, visibleIds, allSelected]);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const colSpanForEmpty = 5 + (selectable ? 1 : 0) + (tagsColumn ? 1 : 0);

  return (
    <StyledTable className="bp3-html-table bp3-html-table-striped">
      <StyledHeader themeFont={headingFont} sticky={stickyHeader} compact>
        <tr>
          {selectable && (
            <HeaderCell center style={{ minWidth: 40, width: 40 }}>
              <Checkbox
                checked={allSelected}
                onChange={() => onToggleAll && onToggleAll(visibleIds)}
                aria-label="Select all"
                inputRef={(el) => {
                  headerCheckboxRef.current = el;
                }}
                style={{ margin: 0 }}
              />
            </HeaderCell>
          )}
          <SortableHeader
            order={filterOptions.name}
            keyName="name"
            label="Name"
            updateFilter={updateFilter}
          />
          <HeaderCell center>Map</HeaderCell>
          {tagsColumn && <HeaderCell>Tags</HeaderCell>}
          <SortableHeader
            order={filterOptions.recordingsUp}
            keyName="recordingsUp"
            label="Online"
            updateFilter={updateFilter}
            center={true}
            tooltipContent="Location Status"
          />
          <SortableHeader
            order={filterOptions.recordingPercent}
            keyName="recordingPercent"
            label="Online Cameras"
            updateFilter={updateFilter}
            center={true}
            tooltipContent="Percentage of Cameras Online"
          />
          <SortableHeader
            order={filterOptions.status}
            keyName="status"
            label="Contract Status"
            updateFilter={updateFilter}
            center={true}
          />
        </tr>
      </StyledHeader>
      <tbody>
        {visibleData && visibleData.length > 0 ? (
          visibleData.map((item, i) => (
            <LocationRow
              {...item}
              index={i}
              key={item.id || item.name}
              selectable={selectable}
              selected={selected ? selected.has(item.id) : false}
              onToggleRow={onToggleRow}
              tagsCell={tagsColumn}
              location={item}
            />
          ))
        ) : (
          <tr>
            <td colSpan={colSpanForEmpty} style={{ textAlign: 'center' }}>
              No data available
            </td>
          </tr>
        )}
      </tbody>
    </StyledTable>
  );
};

export default LocationTable;
